import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { EChartsOption } from 'echarts';
import {
	DashboardsServiceV2,
	WidgetDataDTO,
	WidgetDataSetRequestDTO,
	WidgetDefinitionDTO,
	AxisTypeInd,
} from 'src/app/core/services/dashboardsv2.service';
import { DashboardWidgetLoadingService } from '../../../dashboard-widget-loading-service';

@Component({
    selector: 'app-widget-scatter-graph',
    templateUrl: './widget-scatter.component.html',
    styleUrls: ['./widget-scatter.component.scss'],
})
export class WidgetScatterComponent implements OnChanges, OnDestroy {
    @Input() widgetConfig: WidgetDefinitionDTO;
    @Input() dataSetRequest: WidgetDataSetRequestDTO;
    @Input() loadWidgetData: boolean;

    echartOptions: EChartsOption;
    dashboardService: DashboardsServiceV2;
    widgetData: WidgetDataDTO;
    isMagicTypeGraphFeature: boolean;
    isRestoreGraphFeature: boolean;
    subscription: any;
    isLoaded: boolean = false;
    chartInstance: any; // Added chartInstance

    public lastLoadedDataSet: WidgetDataSetRequestDTO | undefined = undefined;

    constructor(
        dashboardService: DashboardsServiceV2,
        private readonly _dashboardWidgetLoadingService: DashboardWidgetLoadingService
    ) {
        this.dashboardService = dashboardService;
    }

    onChartInit(e: any) {
        this.chartInstance = e;
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void {
        const hasDataSetChangedSinceLastLoaded =
            JSON.stringify(this.dataSetRequest) !== JSON.stringify(this.lastLoadedDataSet);

        if (changes.dataSetRequest && !changes.dataSetRequest.isFirstChange()) {
            if (this.loadWidgetData && this.widgetConfig.version != 3) {
                this.loadData();
            } else {
                this.markAsLoading();
            }
        }

        if (this.loadWidgetData && hasDataSetChangedSinceLastLoaded && this.widgetConfig.version != 3) {
            this.loadData();
        }

        if (changes.loadWidgetData && !this.isLoaded) {
            if (changes.loadWidgetData.currentValue && this.widgetConfig.version != 3) {
                this.loadData();
            }
            //stop loading unfinished widget
            if (!changes.loadWidgetData.currentValue && this.subscription) {
                this.subscription.unsubscribe();
                this.widgetConfig.loading = false;
                this._dashboardWidgetLoadingService.remove(this.widgetConfig.id.toString());
            }
        }

        // Handle updates for version 3 dashboards where data is pushed directly via widgetConfig.data
        if (changes.widgetConfig && this.widgetConfig.version === 3) {
            if (this.widgetConfig.data) {
                this.widgetData = this.widgetConfig.data;
                this.loadData(); // Call loadData to trigger rendering with existing data
            }
        }
    }

    loadData() {
        this.isMagicTypeGraphFeature = this.widgetConfig.magicTypeGraphFeature;
        this.isRestoreGraphFeature = this.widgetConfig.restoreGraphFeature;

        if (this.widgetConfig.version === 3 && this.widgetData) {
            // If version 3 and data is already provided, just build the chart
            this.markAsLoaded();
            this._updateLastLoadedDataSet();
            this.getScatterChartData();

            if (this.chartInstance) {
                this.chartInstance.setOption(this.echartOptions, true);
            }
            return;
        }

        if (this.subscription) {
            this.subscription.unsubscribe();
        }

        this.subscription = this.dashboardService
            .getDataForWidget(this.widgetConfig, this.dataSetRequest)
            .subscribe((response: WidgetDataDTO) => {
                this.widgetData = response;
                this.markAsLoaded();
                this._updateLastLoadedDataSet();

                if (response.data) {
                    this.getScatterChartData();
                }
                if (this.chartInstance) {
                    this.chartInstance.setOption(this.echartOptions, true);
                }
            });
    }

    getScatterChartData() {
        const data = this.widgetData.data;

        let xAxisLbl = '';
        let yAxisLbl = '';

        //Here check the Axis type and set the lable accordingly
        this.widgetData.axis.forEach((x) => {
            if (x.axisType === AxisTypeInd.XAxis) xAxisLbl = x.axisLabel;
            else if (x.axisType === AxisTypeInd.YAxis) yAxisLbl = x.axisLabel;
        });

        this.echartOptions = {
            toolbox: {
                show: true,
                feature: {
                    magicType: {
                        show: this.isMagicTypeGraphFeature,
                        type: ['line', 'bar'],
                    },
                    dataZoom: {
                        show: false,
                    },
                    mark: {
                        show: false,
                    },
                    restore: {
                        show: this.isRestoreGraphFeature,
                    },
                    saveAsImage: {
                        show: true,
                        name: ('SportsEye_' + this.widgetConfig.title.replace(' ', '_') + '_Export').toLowerCase(),
                    },
                },
            },
            grid: {
                containLabel: true,
                left: 55,
                right: 55,
                bottom: 35,
            },
            tooltip: {
                position: 'top',
            },
            xAxis: {
                name: xAxisLbl,
                nameLocation: 'middle',
                nameGap: 30,
            },
            yAxis: {
                name: yAxisLbl,
                nameLocation: 'middle',
                nameGap: 55,
            },
            series: [
                {
                    symbolSize: 20,
                    data: data,
                    type: 'scatter',
                },
            ],
        };
        if (this.chartInstance) {
            this.chartInstance.setOption(this.echartOptions, true);
        }
    }

    public markAsLoading(): void {
        this.isLoaded = false;

        const widgetId = this.widgetConfig.id.toString();

        this._dashboardWidgetLoadingService.add(widgetId);
    }

    public markAsLoaded(): void {
        this.isLoaded = true;

        const widgetId = this.widgetConfig.id.toString();

        this._dashboardWidgetLoadingService.remove(widgetId);
    }

    private _updateLastLoadedDataSet(): void {
        this.lastLoadedDataSet = { ...this.dataSetRequest };
    }
}
